import { createCsvHeaderResolver } from './csv-header'

/**
 * スキルチェック評価の定義（唯一の情報源）。
 * 顧客のスキルチェックシート「ガイドライン」シートに準拠し、backend（検証・集計）と frontend（入力・表示）の双方が参照する。
 */

/** 評価基準（高い順） */
export const SKILL_SCORE_OPTIONS = [
  { score: 5, label: 'よくできている' },
  { score: 4, label: 'ややできている' },
  { score: 3, label: 'どちらともいえない' },
  { score: 2, label: 'あまりできていない' },
  { score: 1, label: 'まったくできていない' },
] as const

export type SkillScore = (typeof SKILL_SCORE_OPTIONS)[number]['score']

const SKILL_SCORES = SKILL_SCORE_OPTIONS.map((o) => o.score)
export const SKILL_SCORE_MIN = Math.min(...SKILL_SCORES)
export const SKILL_SCORE_MAX = Math.max(...SKILL_SCORES)

export const EVALUATOR_TYPES = ['self', 'supervisor'] as const
export type EvaluatorType = (typeof EVALUATOR_TYPES)[number]

export const EVALUATOR_TYPE_LABELS: Record<EvaluatorType, string> = {
  self: '本人',
  supervisor: '上司',
}

/** returned = 人事による差し戻し（再提出待ち） */
export const EVALUATION_STATUSES = ['submitted', 'returned'] as const
export type EvaluationStatus = (typeof EVALUATION_STATUSES)[number]

/**
 * フォローアップの回の定義。期日の起点は、1回目は研修の最終日、2回目以降は前の回の期日。
 * 期日 = 起点 + dueAfterMonths（暦月）+ dueAfterDays（当日を含む提出期日）、回答は期日の openDaysBeforeDue 日前から。
 * 期日後も提出でき、リマインダー有効時は未提出の評価者に毎日リマインドする（BIZ-F02 / FR-7.6）。
 */
export interface FollowUpPhaseDefinition {
  label: string
  dueAfterMonths: number
  dueAfterDays: number
  openDaysBeforeDue: number
}

/** 例: 9/3 の研修 → 受講直後は 9/3〜9/17、受講3か月後は 12/3〜12/17 */
export const DEFAULT_FOLLOW_UP_PHASES: readonly FollowUpPhaseDefinition[] = [
  { label: '受講直後', dueAfterMonths: 0, dueAfterDays: 14, openDaysBeforeDue: 14 },
  { label: '受講3か月後', dueAfterMonths: 3, dueAfterDays: 0, openDaysBeforeDue: 14 },
]

/** スキルチェック項目CSVの列の正規キー */
export const TRAINING_SKILL_ITEM_CSV_FIELDS = ['code', 'category', 'label'] as const
export type TrainingSkillItemCsvField = (typeof TRAINING_SKILL_ITEM_CSV_FIELDS)[number]

/** 正規キー → 受け付けるヘッダー表記（スキルチェックシート・人事の集計表の見出しに合わせる） */
export const TRAINING_SKILL_ITEM_CSV_HEADER_ALIASES: Record<TrainingSkillItemCsvField, string[]> = {
  code: ['項目コード', '項目ID', 'code'],
  category: ['大項目', 'category'],
  label: ['具体項目', '項目名', 'label'],
}

/** 表示用の日本語ラベル（エラーメッセージ・テンプレートの見出し） */
export const TRAINING_SKILL_ITEM_CSV_FIELD_LABELS: Record<TrainingSkillItemCsvField, string> = {
  code: '項目コード',
  category: '大項目',
  label: '具体項目',
}

export const TRAINING_SKILL_ITEM_CSV_TEMPLATE_HEADERS = TRAINING_SKILL_ITEM_CSV_FIELDS.map(
  (field) => TRAINING_SKILL_ITEM_CSV_FIELD_LABELS[field],
)

export const toTrainingSkillItemCsvField = createCsvHeaderResolver(
  TRAINING_SKILL_ITEM_CSV_FIELDS,
  TRAINING_SKILL_ITEM_CSV_HEADER_ALIASES,
)
